/**
 * Controles da frente do cartão, a dica de gesto e a lista de atalhos.
 * Só apresenta: não lê a sessão, não chama a API e não decide o que a tecla
 * faz quando o foco está num campo — isso fica na view.
 */
import { ArrowRight, RotateCw } from "lucide-react";
import type { Rating } from "../../../sdk/@types/Revisao";
import { revisaoPrimaryActionClass } from "./revisaoSurfaces";

export type ComandoSessao =
  | { tipo: "virar" }
  | { tipo: "pular" }
  | { tipo: "nota"; rating: Rating };

export interface AtalhoSessao {
  acao: string;
  tecla: string;
}

/** The shortcuts this screen actually handles. The list and the key handler both read this so they cannot diverge. */
export const ATALHOS_SESSAO: readonly AtalhoSessao[] = [
  { acao: "Virar", tecla: "Espaço" },
  { acao: "Pular", tecla: "→" },
  { acao: "Errei", tecla: "1" },
  { acao: "Difícil", tecla: "2" },
  { acao: "Bom", tecla: "3" },
  { acao: "Fácil", tecla: "4" },
];

const NOTA_POR_TECLA: Record<string, Rating> = {
  "1": "again",
  "2": "hard",
  "3": "good",
  "4": "easy",
};

const SECUNDARIO_CLASS =
  "inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full border border-edge bg-surface px-6 text-sm font-medium text-ink hover:bg-surface-hover sm:w-auto";

function elementoDoAlvo(alvo: EventTarget | null): Element | null {
  if (alvo instanceof Element) return alvo;
  if (alvo instanceof Node) return alvo.parentElement;
  return null;
}

/** True when the event target is a text field. Shortcuts must not run in that case. */
export function focoEmCampoDeTexto(alvo: EventTarget | null): boolean {
  const el = elementoDoAlvo(alvo);
  if (!el) return false;

  if (el instanceof HTMLElement) {
    const tag = el.tagName.toUpperCase();
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable) {
      return true;
    }
  }

  if (el.closest("input, textarea, select")) return true;

  const hospedeiro = el.closest("[contenteditable]");
  return hospedeiro instanceof HTMLElement && hospedeiro.isContentEditable;
}

/**
 * Space → virar. ArrowRight → pular. 1/2/3/4 → the four ratings, and ONLY when `virado` is true.
 * meta/ctrl/alt → null. Shift+· on a digit → null (a shifted digit is not a rating).
 * Do not map Enter, "f", or ArrowLeft.
 * Do not map "e": the study view opens the editor for an administrator, and
 * only when this function did not claim the key. Mapping it here would fire
 * for every profile.
 * This function does not look at focus; the view checks `focoEmCampoDeTexto` first.
 */
export function comandoDaTecla(
  event: { key: string; metaKey?: boolean; ctrlKey?: boolean; altKey?: boolean; shiftKey?: boolean },
  virado: boolean,
): ComandoSessao | null {
  if (event.metaKey || event.ctrlKey || event.altKey) return null;

  if (event.key === " " || event.key === "Spacebar") return { tipo: "virar" };
  if (event.key === "ArrowRight") return { tipo: "pular" };

  const rating = NOTA_POR_TECLA[event.key];
  if (!rating) return null;
  if (event.shiftKey || !virado) return null;
  return { tipo: "nota", rating };
}

export interface SessaoControlesProps {
  /** When false, Desfazer is not in the DOM at all — not disabled, not aria-hidden. */
  podeDesfazer: boolean;
  onDesfazer: () => void;
  onVirar: () => void;
  onPular: () => void;
  /** While a rating POST is in flight. Pular is disabled. Virar stays available. Desfazer is already absent because the hook clears `podeDesfazer` while recording. */
  ocupado?: boolean;
}

export function SessaoControles({
  podeDesfazer,
  onDesfazer,
  onVirar,
  onPular,
  ocupado = false,
}: SessaoControlesProps): JSX.Element {
  return (
    <div
      data-sessao-controles=""
      role="group"
      aria-label="Controles do cartão"
      className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center"
    >
      {podeDesfazer ? (
        <button
          type="button"
          data-sessao-controle="desfazer"
          onClick={onDesfazer}
          className={SECUNDARIO_CLASS}
        >
          Desfazer
        </button>
      ) : null}
      <button
        type="button"
        data-sessao-controle="virar"
        onClick={onVirar}
        aria-keyshortcuts="Space"
        className={`${revisaoPrimaryActionClass} w-full gap-2 sm:w-auto`}
      >
        <RotateCw size={16} aria-hidden="true" />
        Virar
      </button>
      <button
        type="button"
        data-sessao-controle="pular"
        onClick={onPular}
        aria-keyshortcuts="ArrowRight"
        disabled={ocupado}
        className={`${SECUNDARIO_CLASS} disabled:cursor-not-allowed disabled:opacity-40`}
      >
        <ArrowRight size={16} aria-hidden="true" />
        Pular
      </button>
    </div>
  );
}

export function DicaGesto(): JSX.Element {
  return (
    <p data-dica-gesto="" className="text-center text-sm text-ink-secondary">
      Arraste para pular sem avaliar.
    </p>
  );
}

export function ListaAtalhosSessao({
  atalhosExtra = [],
}: {
  /** Shown after the session shortcuts. The edit shortcut is passed only for an administrator. */
  atalhosExtra?: readonly AtalhoSessao[];
} = {}): JSX.Element {
  const atalhos = atalhosExtra.length > 0 ? [...ATALHOS_SESSAO, ...atalhosExtra] : ATALHOS_SESSAO;
  return (
    <section
      data-atalhos-sessao=""
      aria-label="Atalhos de teclado"
      className="flex w-full flex-col gap-3 rounded-card border border-edge bg-surface p-4"
    >
      <h2 className="text-xs font-semibold tracking-wide text-ink-muted">Atalhos</h2>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {atalhos.map((atalho) => (
          <li key={atalho.tecla} className="flex items-center justify-between gap-3">
            <span className="text-sm text-ink-secondary">{atalho.acao}</span>
            <kbd
              data-atalho={atalho.tecla}
              className="rounded-chip border border-edge bg-surface-muted px-2 py-0.5 font-mono text-xs text-ink-secondary"
            >
              {atalho.tecla}
            </kbd>
          </li>
        ))}
      </ul>
    </section>
  );
}
